// P2b tanda 6 — EVALUADOR DE COMPLETITUD de la ficha de producto. PURO y CJS a
// propósito: lo consumen el panel de la ficha (en vivo, sin red) y el BACKEND
// (materialización al guardar y en Recalcular todo) — mismo patrón compartido
// que groupOrdersByProduct. Una sola definición de las reglas para las dos
// puntas; los adaptadores de entrada (form de Neto / producto crudo de Woo)
// viven acá mismo para que nadie arme la entrada a mano.
//
// Niveles: 'obligatoria' (bloquea guardar en Woo — ya lo hace requiredToSaveWoo,
// acá solo se REFLEJA), 'recomendada' (no bloquea), y reglas EXCLUIDAS por el
// flag de configuración catalogo.vitrinaActiva (sección Vitrina y guía de
// tallas no cuentan mientras Vitrina no esté en producción — flag por config,
// no por código). Una regla pospuesta ("después de guardar") no cuenta en los
// totales: no se puede cumplir todavía y contarla sería castigar sin salida.
//
// Nota medida (no inventar checks): la PROPORCIÓN 4:5 de las imágenes no viaja
// en los datos (ni /full ni el fetch de productos traen dimensiones de imagen);
// la garantiza el flujo de subida (ajuste 4:5 de la 3e). La regla de imagen
// verifica presencia + alt, y lo dice en su mensaje.

const { tipoActivo, aplicaContenidoDeTipo } = require('./tiposProducto');
const { isAllCaps, validateGtin, validateSku } = require('./productValidation');

const num = (v) => String(v == null ? '' : v).trim();
const positivo = (v) => { const n = Number(num(v)); return Number.isFinite(n) && n > 0; };
const gtinOk = (v) => num(v) !== '' && validateGtin(v).ok;

// ── Entrada NEUTRAL del evaluador ─────────────────────────────────────────────
// { esNuevo, esVariable, nombre, shortDescription, descriptionHtml, sku, gtin,
//   regularPrice, stockQty, weight, dims:{length,width,height},
//   imagenes:[{alt}], categoriasIds:[], brandsCount, crossSellCount|null,
//   seoTitle, seoMetadesc, contenidoValor, contenidoUnidad, productKind,
//   variaciones:[{sku, gtin, precio, stock, tieneImagen}], guiaOrigen|null }

// Adaptador 1: el FORM de la ficha de Neto (+ filas de variaciones + extras).
function entradaDesdeForm(form, vRows, extras = {}) {
  const f = form || {};
  return {
    esNuevo: extras.esNuevo === true,
    esVariable: f.type === 'variable',
    nombre: f.name || '',
    shortDescription: f.shortDescription || '',
    descriptionHtml: f.description || '',
    sku: f.sku || '',
    gtin: f.gtin || '',
    regularPrice: f.regularPrice || '',
    stockQty: f.stockQty ?? '',
    weight: f.weight || '',
    dims: { length: f.length || '', width: f.width || '', height: f.height || '' },
    imagenes: (f.images || []).map((i) => ({ alt: i.alt || '' })),
    categoriasIds: f.categoryIds || [],
    brandsCount: f.brandId ? 1 : 0,
    coleccionesCount: Array.isArray(f.collectionTagIds) ? f.collectionTagIds.length : 0, // P3-4
    crossSellCount: Array.isArray(f.crossSellIds) ? f.crossSellIds.length : (extras.crossSellCount ?? null),
    seoTitle: f.seoTitle || '',
    seoMetadesc: f.seoMetadesc || '',
    contenidoValor: f.contenidoValor || '',
    contenidoUnidad: f.contenidoUnidad || '',
    productKind: f.productKind || '',
    variaciones: (vRows || []).map((r) => ({
      nombre: r.option || '',
      sku: r.sku || '',
      gtin: r.gtin || '',
      precio: r.regularPrice || '',
      stock: r.stockQty ?? '',
      tieneImagen: !!(r.image && (r.image.src || r.image.id)),
    })),
    guiaOrigen: extras.guiaOrigen ?? null,
  };
}

// Adaptador 2: el producto CRUDO de WooCommerce (+ variaciones crudas) — lo usa
// el backend para materializar. `tipos` resuelve el productKind desde la meta
// _vitrina_arquetipo (el mismo mapeo de productToForm, sin la deducción por
// categorías: para EVALUAR alcanza el arquetipo explícito o el primer tipo).
function entradaDesdeWoo(raw, variacionesRaw, tipos) {
  const p = raw || {};
  const meta = (k) => {
    const m = (p.meta_data || []).find((x) => x && x.key === k);
    return m ? m.value : null;
  };
  const arquetipo = meta('_vitrina_arquetipo');
  const tipoDeArquetipo = (tipos || []).find((t) => t && t.arquetipo === arquetipo);
  const contenido = meta('_vitrina_contenido');
  return {
    esNuevo: false,
    esVariable: p.type === 'variable',
    nombre: p.name || '',
    shortDescription: String(p.short_description || '').replace(/<[^>]+>/g, '').trim(),
    descriptionHtml: p.description || '',
    sku: p.sku || '',
    gtin: p.global_unique_id || '',
    regularPrice: p.regular_price || '',
    stockQty: p.stock_quantity ?? '',
    weight: p.weight || '',
    dims: {
      length: (p.dimensions && p.dimensions.length) || '',
      width: (p.dimensions && p.dimensions.width) || '',
      height: (p.dimensions && p.dimensions.height) || '',
    },
    imagenes: (p.images || []).map((i) => ({ alt: (i && i.alt) || '' })),
    categoriasIds: (p.categories || []).map((c) => c.id),
    brandsCount: (p.brands || []).length,
    coleccionesCount: (p.tags || []).filter((t) => t && typeof t.slug === 'string' && t.slug.toLowerCase().startsWith('coleccion-')).length, // P3-4
    crossSellCount: Array.isArray(p.cross_sell_ids) ? p.cross_sell_ids.length : null,
    seoTitle: String(meta('_yoast_wpseo_title') || ''),
    seoMetadesc: String(meta('_yoast_wpseo_metadesc') || ''),
    contenidoValor: contenido && typeof contenido === 'object' ? String(contenido.valor || '') : '',
    contenidoUnidad: contenido && typeof contenido === 'object' ? String(contenido.unidad || '') : '',
    productKind: (tipoDeArquetipo && tipoDeArquetipo.id) || ((tipos || [])[0] && tipos[0].id) || '',
    variaciones: (variacionesRaw || []).map((v) => ({
      nombre: ((v.attributes || [])[0] && v.attributes[0].option) || '',
      sku: v.sku || '',
      gtin: v.global_unique_id || '',
      precio: v.regular_price || '',
      stock: v.stock_quantity ?? '',
      tieneImagen: !!(v.image && (v.image.src || v.image.id)),
    })),
    guiaOrigen: null, // la materialización no consulta el árbitro del theme
  };
}

// ── Las reglas ────────────────────────────────────────────────────────────────
// Cada una: { id, nombre, nivel, destino:{tab, foco}, evaluar(e, ctx) →
//   { aplicable, cumplida, mensaje?, pospuesta? } }. `pospuesta` = "después de
//   guardar": visible en el panel, fuera de los totales.

const REGLAS = [
  {
    id: 'nombre', nombre: 'Nombre', nivel: 'obligatoria', destino: { tab: 'general', foco: 'name' },
    evaluar: (e) => ({ aplicable: true, cumplida: num(e.nombre) !== '', mensaje: 'El producto necesita nombre.' }),
  },
  {
    id: 'precio', nombre: 'Precio', nivel: 'obligatoria', destino: { tab: 'general', foco: 'regularPrice' },
    evaluar: (e) => {
      if (!e.esVariable) return { aplicable: true, cumplida: positivo(e.regularPrice), mensaje: 'Falta el precio (mayor que 0).' };
      const ok = e.variaciones.length > 0 && e.variaciones.every((v) => positivo(v.precio));
      return { aplicable: true, cumplida: ok, mensaje: 'Cada variación necesita su precio.' };
    },
  },
  {
    id: 'cantidad', nombre: 'Cantidad', nivel: 'obligatoria', destino: { tab: 'general', foco: 'stockQty' },
    evaluar: (e) => {
      if (!e.esVariable) return { aplicable: true, cumplida: num(e.stockQty) !== '', mensaje: 'Falta la cantidad en stock.' };
      const ok = e.variaciones.length > 0 && e.variaciones.every((v) => num(v.stock) !== '');
      return { aplicable: true, cumplida: ok, mensaje: 'Cada variación necesita su stock.' };
    },
  },
  {
    id: 'nombre-formato', nombre: 'Nombre bien escrito', nivel: 'recomendada', destino: { tab: 'general', foco: 'name' },
    evaluar: (e) => {
      const s = num(e.nombre);
      if (!s) return { aplicable: true, cumplida: false, mensaje: 'Sin nombre no hay formato que revisar.' };
      const sinCaps = !isAllCaps(s);
      const sinContenido = !/\b\d+\s*(ml|g|gr|oz|kg|l)\b/i.test(s) && !/\bml\b/i.test(s);
      const palabras = s.toLowerCase().split(/\s+/);
      const marcaUnaVez = palabras.filter((w) => w === palabras[0]).length <= 1;
      return {
        aplicable: true, cumplida: sinCaps && sinContenido && marcaUnaVez,
        mensaje: 'Sin mayúsculas sostenidas, sin ml/g en el nombre y la marca una sola vez.',
      };
    },
  },
  {
    id: 'descripcion-corta', nombre: 'Descripción corta', nivel: 'recomendada', destino: { tab: 'general', foco: 'shortDescription' },
    evaluar: (e) => {
      const n = num(e.shortDescription).length;
      const ok = n >= 1 && n <= 150;
      return {
        aplicable: true, cumplida: ok, mensaje: 'Una o dos frases (1–150 caracteres).',
        detalle: ok ? '' : (n === 0 ? 'Escribe 1–2 frases de qué es' : `Recórtala a 150 caracteres (vas en ${n})`),
      };
    },
  },
  {
    id: 'descripcion-larga', nombre: 'Descripción larga completa', nivel: 'recomendada', destino: { tab: 'general', foco: 'descParrafo' },
    evaluar: (e) => {
      const html = String(e.descriptionHtml || '');
      const parrafos = (html.match(/<p[\s>]/gi) || []).length;
      const conLista = /<ul[\s>][\s\S]*?<li[\s>]/i.test(html);
      const faltas = [];
      if (parrafos < 1) faltas.push('el párrafo');
      if (!conLista) faltas.push('la lista de detalles');
      if (parrafos < 2) faltas.push('el bloque final');
      return {
        aplicable: true, cumplida: faltas.length === 0,
        mensaje: 'Párrafo + detalles en lista + último bloque.',
        detalle: faltas.length ? `Falta: ${faltas.join(' y ')}` : '',
      };
    },
  },
  {
    id: 'imagen-principal', nombre: 'Imagen principal con alt', nivel: 'recomendada', destino: { tab: 'otras', foco: 'galeria' },
    evaluar: (e) => {
      const sinImagen = e.imagenes.length === 0;
      const sinAlt = !sinImagen && num(e.imagenes[0].alt) === '';
      return {
        aplicable: true,
        cumplida: !sinImagen && !sinAlt,
        // La proporción 4:5 la garantiza el flujo de subida; no viaja en los datos.
        mensaje: 'Imagen principal con texto alternativo (el 4:5 lo asegura la subida).',
        detalle: sinImagen ? 'Falta: la imagen principal' : (sinAlt ? 'Falta: el texto alternativo' : ''),
      };
    },
  },
  {
    id: 'galeria', nombre: 'Galería de 2 a 6 fotos', nivel: 'recomendada', destino: { tab: 'otras', foco: 'galeria' },
    evaluar: (e) => {
      const n = Math.max(0, e.imagenes.length - 1);
      const ok = n >= 2 && n <= 6;
      return {
        aplicable: true, cumplida: ok, mensaje: 'Además de la principal, de 2 a 6 fotos.',
        detalle: ok ? '' : (n < 2 ? 'Agrega 2 a 6 fotos' : `Deja máximo 6 (tienes ${n})`),
      };
    },
  },
  {
    id: 'variaciones-completas', nombre: 'Variaciones completas', nivel: 'recomendada', destino: { tab: 'general', foco: 'variaciones' },
    evaluar: (e) => {
      if (!e.esVariable) return { aplicable: false, cumplida: true };
      const faltas = [];
      e.variaciones.forEach((v, i) => {
        const de = [];
        if (!v.tieneImagen) de.push('sin foto');
        if (num(v.sku) === '') de.push('sin SKU');
        if (!positivo(v.precio)) de.push('sin precio');
        if (num(v.stock) === '') de.push('sin stock');
        if (de.length) faltas.push(`${v.nombre || `Variación ${i + 1}`} ${de.join(' y ')}`);
      });
      const ok = e.variaciones.length > 0 && faltas.length === 0;
      return {
        aplicable: true, cumplida: ok, mensaje: 'Cada variación con imagen propia, SKU, precio y stock.',
        detalle: ok ? '' : (e.variaciones.length === 0 ? 'El producto variable no tiene variaciones' : `Faltan: ${faltas.slice(0, 4).join(', ')}${faltas.length > 4 ? '…' : ''}`),
      };
    },
  },
  {
    id: 'categoria-especifica', nombre: 'Categoría específica', nivel: 'recomendada', destino: { tab: 'organizacion', foco: 'categorias' },
    evaluar: (e, ctx) => {
      const porId = new Map((ctx.categorias || []).map((c) => [c.id, c]));
      const tieneHoja = (e.categoriasIds || []).some((id) => {
        const c = porId.get(id);
        return c && c.parent !== 0;
      });
      return { aplicable: true, cumplida: tieneHoja, mensaje: 'Marca una categoría hoja, no solo la general.' };
    },
  },
  {
    id: 'marca', nombre: 'Marca', nivel: 'recomendada', destino: { tab: 'organizacion', foco: 'marca' },
    evaluar: (e, ctx) => {
      if (!ctx.tipo || !ctx.tipo.marcaObligatoria) return { aplicable: false, cumplida: true };
      return { aplicable: true, cumplida: e.brandsCount > 0, mensaje: `${ctx.tipo.nombre} pide marca.` };
    },
  },
  {
    // P3-4: colección — el tipo decide si la cuenta (pideColeccion), como marca.
    id: 'coleccion', nombre: 'Colección', nivel: 'recomendada', destino: { tab: 'organizacion', foco: 'colecciones' },
    evaluar: (e, ctx) => {
      if (!ctx.tipo || !ctx.tipo.pideColeccion) return { aplicable: false, cumplida: true };
      return { aplicable: true, cumplida: (e.coleccionesCount || 0) > 0, mensaje: `${ctx.tipo.nombre} pide colección.` };
    },
  },
  {
    id: 'codigo-barras', nombre: 'Código de barras', nivel: 'recomendada', destino: { tab: 'general', foco: 'gtin' },
    evaluar: (e, ctx) => {
      if (!ctx.tipo || !ctx.tipo.aplicaGtin) return { aplicable: false, cumplida: true };
      if (!e.esVariable) {
        return {
          aplicable: true, cumplida: gtinOk(e.gtin), mensaje: 'GTIN/EAN de 8, 12, 13 o 14 dígitos.',
          detalle: gtinOk(e.gtin) ? '' : (num(e.gtin) === '' ? 'Falta: el código de barras' : 'El largo es inválido (va 8, 12, 13 o 14 dígitos)'),
        };
      }
      const sinGtin = e.variaciones.map((v, i) => (gtinOk(v.gtin) ? null : (v.nombre || `Variación ${i + 1}`))).filter(Boolean);
      const ok = e.variaciones.length > 0 && sinGtin.length === 0;
      return {
        aplicable: true, cumplida: ok, mensaje: 'Cada variación con su GTIN/EAN válido.',
        detalle: ok ? '' : `Faltan: ${sinGtin.slice(0, 4).join(', ')}${sinGtin.length > 4 ? '…' : ''}`,
      };
    },
  },
  {
    id: 'sku-formato', nombre: 'SKU con formato', nivel: 'recomendada', destino: { tab: 'general', foco: 'sku' },
    evaluar: (e) => ({ aplicable: true, cumplida: validateSku(e.sku).ok, mensaje: 'SKU con mayúsculas, números y guiones.' }),
  },
  {
    id: 'peso-dimensiones', nombre: 'Peso y dimensiones', nivel: 'recomendada', destino: { tab: 'inventario', foco: 'weight' },
    evaluar: (e) => {
      const sinPeso = !positivo(e.weight);
      const sinDims = !(positivo(e.dims.length) && positivo(e.dims.width) && positivo(e.dims.height));
      return {
        aplicable: true, cumplida: !sinPeso && !sinDims,
        mensaje: 'Peso y las tres dimensiones mayores que 0 (cotizan el envío).',
        detalle: sinPeso && sinDims ? 'Falta: el peso y las dimensiones' : sinPeso ? 'Falta: el peso' : sinDims ? 'Falta: las dimensiones' : '',
      };
    },
  },
  {
    // Tanda 7b (ítem 2): UN solo nombre en toda la interfaz — el de la sección.
    // El id NO se renombra: los FichaEstado materializados en Mongo guardan
    // pendientesIds con este id.
    id: 'ventas-cruzadas', nombre: 'Productos relacionados (Combínalo con)', nivel: 'recomendada', destino: { tab: 'organizacion', foco: 'relacionados' },
    evaluar: (e) => {
      // Tanda 7: la sección "Productos relacionados" se edita EN el form, así
      // que la regla cuenta en vivo; pospuesta solo si el dato no existe
      // (entradas legadas sin crossSellIds ni cross_sell_ids).
      if (e.crossSellCount == null) return { aplicable: false, cumplida: false, pospuesta: true, mensaje: 'Después de guardar.' };
      const n = e.crossSellCount;
      const ok = n >= 2 && n <= 3;
      return {
        aplicable: true, cumplida: ok, mensaje: '2 o 3 productos relacionados.',
        detalle: ok ? '' : (n === 0 ? 'Elige 2 o 3 que se usen juntos' : n === 1 ? 'Tienes 1; agrega 1 o 2 más' : `Tienes ${n}; deja solo 2 o 3`),
      };
    },
  },
  {
    id: 'seo', nombre: 'Título SEO y meta description', nivel: 'recomendada', destino: { tab: 'seo', foco: 'seoTitle' },
    evaluar: (e) => {
      const t = num(e.seoTitle).length;
      const d = num(e.seoMetadesc).length;
      const faltas = [];
      if (t === 0) faltas.push('título SEO'); else if (t > 60) faltas.push(`título SEO más corto (vas en ${t}/60)`);
      if (d === 0) faltas.push('meta description'); else if (d > 150) faltas.push(`meta description más corta (vas en ${d}/150)`);
      return {
        aplicable: true, cumplida: faltas.length === 0, mensaje: 'Título (1–60) y meta description (1–150).',
        detalle: faltas.length ? `Falta: ${faltas.join(' y ')}` : '',
      };
    },
  },
  {
    id: 'contenido', nombre: 'Contenido (ml/g)', nivel: 'recomendada', destino: { tab: 'general', foco: 'contenido' },
    evaluar: (e, ctx) => {
      // Tanda 8c (J9): mismo criterio que la columna de la ficha (flag + fallback).
      if (!aplicaContenidoDeTipo(ctx.tipo)) return { aplicable: false, cumplida: true };
      return { aplicable: true, cumplida: positivo(e.contenidoValor) && num(e.contenidoUnidad) !== '', mensaje: 'Valor y unidad del contenido (30 ml, 50 g).' };
    },
  },
  // Excluidas por el flag catalogo.vitrinaActiva: mientras Vitrina no esté en
  // producción NO CUENTAN (se listan aparte en el panel, nunca en el total).
  {
    id: 'guia-tallas', nombre: 'Guía de tallas', nivel: 'recomendada', destino: { tab: 'general', foco: 'guia' },
    evaluar: (e, ctx) => {
      if (!ctx.vitrinaActiva || !ctx.tipo || !ctx.tipo.aplicaTallas) return { aplicable: false, cumplida: true };
      if (e.guiaOrigen == null) return { aplicable: false, cumplida: false, pospuesta: true, mensaje: 'Después de guardar.' };
      return { aplicable: true, cumplida: e.guiaOrigen !== 'ninguna', mensaje: 'El producto no resuelve ninguna guía.' };
    },
  },
  {
    id: 'vitrina', nombre: 'Sección Vitrina', nivel: 'recomendada', destino: { tab: 'otras', foco: 'vitrina' },
    // Su contenido (atributos, cuidados, cómo usar) llega con Vitrina activo;
    // hasta entonces no cuenta ni cuando el flag se encienda hay qué medir acá.
    evaluar: () => ({ aplicable: false, cumplida: true }),
  },
];

const NOMBRES = Object.fromEntries(REGLAS.map((r) => [r.id, r.nombre]));

// ── El evaluador ─────────────────────────────────────────────────────────────
// entrada: la forma neutral (usar los adaptadores). opciones: { tipos,
// categorias (catálogo [{id,parent}]), vitrinaActiva }.
function evaluarFicha(entrada, opciones = {}) {
  const tipos = opciones.tipos || [];
  const ctx = {
    tipo: tipoActivo(tipos, entrada.productKind),
    categorias: opciones.categorias || [],
    vitrinaActiva: opciones.vitrinaActiva === true,
  };
  const reglas = REGLAS.map((r) => {
    const res = r.evaluar(entrada, ctx);
    return {
      id: r.id, nombre: r.nombre, nivel: r.nivel, destino: r.destino,
      aplicable: !!res.aplicable, cumplida: !!res.cumplida,
      pospuesta: !!res.pospuesta, mensaje: res.mensaje || '',
      // La SEGUNDA LÍNEA del panel: qué hacer o qué parte exacta falta (las
      // compuestas la calculan; nunca un genérico para una compuesta).
      detalle: res.detalle || res.mensaje || '',
    };
  });
  const cuentan = reglas.filter((r) => r.aplicable && !r.pospuesta);
  const cumplidas = cuentan.filter((r) => r.cumplida);
  return {
    reglas,
    aplicables: cuentan.length,
    cumplidas: cumplidas.length,
    pendientesIds: cuentan.filter((r) => !r.cumplida).map((r) => r.id),
    obligatoriasPendientes: cuentan.filter((r) => r.nivel === 'obligatoria' && !r.cumplida),
    recomendadasPendientes: cuentan.filter((r) => r.nivel === 'recomendada' && !r.cumplida),
    pospuestas: reglas.filter((r) => r.pospuesta),
  };
}

module.exports = { evaluarFicha, entradaDesdeForm, entradaDesdeWoo, NOMBRES, REGLAS };
